import React, { useState, useEffect } from 'react';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useNotify,
  useDataProvider,
} from 'react-admin';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Award,
  Star,
  Calendar,
  TrendingUp,
  CheckCircle,
  Clock,
  Ban,
  Pause,
  User,
  FileText,
  Video,
  Image as ImageIcon,
  ExternalLink,
  Copy,
} from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { useNavigate, Link } from 'react-router-dom';
import { logger, LogCategory } from '../utils/logger';
import { CustomDataProvider } from '../types/dataProvider';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { AvatarName, Stars as StarsCell, Money } from '../components/ui/data-cells';
import { useGetList } from 'react-admin';
import { cn } from '../lib/utils';
import { CertStatusBadge, fmtDate, SpecialtyCertificate } from './specialtyCertificates';

// Interfaces
interface Trainer {
  id: number;
  name: string;
  email: string;
  username: string;
  profile_picture_url?: string;
  is_trainer: boolean;
  profile_completed_at: string | null;
  trainer_specialties: string[];
  trainer_hourly_rate: number;
  trainer_availability_days: string[];
  average_rating: number;
  reviews_count: number;
  sessions_completed: number;
  is_suspended: boolean;
  is_banned: boolean;
  is_active: boolean;
  created_at: string;
}

interface TrainerDetails extends Trainer {
  phone: string;
  trainer_bio: string;
  total_sessions: number;
  completed_sessions: number;
  cancelled_sessions: number;
  total_reviews: number;
  suspension_reason: string | null;
  suspension_until: string | null;
  banned_at: string | null;
}

interface TrainerApplication {
  id: number;
  user_id: number;
  bio: string;
  id_document_front_url: string;
  id_document_back_url: string;
  video_url: string | null;
  certificate_urls: string;
  class_photo_urls: string | null;
  status: string;
  created_at: string;
}

// Status badge styling
const getOnboardingStatusStyle = (trainer: Trainer) => {
  if (trainer.is_banned) {
    return 'bg-black text-white border-black';
  }
  if (trainer.is_suspended) {
    return 'bg-purple-50 text-purple-700 border border-purple-200';
  }
  if (!trainer.profile_completed_at) {
    return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
  }
  return 'bg-green-50 text-green-700 border border-green-200';
};

const getOnboardingStatusText = (trainer: Trainer) => {
  if (trainer.is_banned) return 'BANNED';
  if (trainer.is_suspended) return 'SUSPENDED';
  if (!trainer.profile_completed_at) return 'INCOMPLETE';
  return 'ACTIVE';
};

const getOnboardingStatusIcon = (trainer: Trainer) => {
  if (trainer.is_banned) return <Ban className="w-3 h-3" />;
  if (trainer.is_suspended) return <Pause className="w-3 h-3" />;
  if (!trainer.profile_completed_at) return <Clock className="w-3 h-3" />;
  return <CheckCircle className="w-3 h-3" />;
};

// Trainer status badge (design tokens)
const TONE_BY_TRAINER: Record<string, { cls: string; dot: string }> = {
  ACTIVE: { cls: 'bg-success-bg text-success', dot: 'bg-success' },
  INCOMPLETE: { cls: 'bg-warning-bg text-warning', dot: 'bg-warning' },
  SUSPENDED: { cls: 'bg-error-bg text-error', dot: 'bg-error' },
  BANNED: { cls: 'bg-error-bg text-error', dot: 'bg-error' },
};
const TrainerStatusBadge = ({ trainer }: { trainer: Trainer }) => {
  const text = getOnboardingStatusText(trainer);
  const tone = TONE_BY_TRAINER[text] || TONE_BY_TRAINER.INCOMPLETE;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold capitalize', tone.cls)}>
      <span className={cn('size-1.5 rounded-full', tone.dot)} aria-hidden />
      {text.toLowerCase()}
    </span>
  );
};

const TRAINER_TABS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'banned', label: 'Banned' },
];
const TRAINER_SORT = [
  { field: 'sessions_completed', label: 'Sessions' },
  { field: 'average_rating', label: 'Rating' },
  { field: 'created_at', label: 'Joined' },
  { field: 'id', label: 'ID' },
];
const TRAINER_SORT_LABELS = Object.fromEntries(TRAINER_SORT.map((f) => [f.field, f.label]));

const TrainerStatusTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status_filter as string) || 'all';
  return (
    <div className="max-w-full overflow-x-auto">
      <SegmentedTabs
        tabs={TRAINER_TABS}
        value={current}
        onChange={(v) =>
          setFilters({ ...filterValues, status_filter: v === 'all' ? undefined : v }, displayedFilters)
        }
      />
    </div>
  );
};

const TrainerStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const active = useGetList('trainers', { ...base, filter: { status_filter: 'active' } });
  const incomplete = useGetList('trainers', { ...base, filter: { status_filter: 'incomplete' } });
  const suspended = useGetList('trainers', { ...base, filter: { status_filter: 'suspended' } });
  const all = useGetList('trainers', { ...base, filter: {} });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Active coaches" value={n(active.total)} />
      <StatCard label="Incomplete" value={n(incomplete.total)} />
      <StatCard label="Suspended" value={n(suspended.total)} />
      <StatCard label="Total trainers" value={n(all.total)} />
    </StatRow>
  );
};

const TrainerListBody = () => {
  const columns: DataColumn<Trainer>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'trainer',
      header: 'Trainer',
      cell: (r) => <AvatarName src={r.profile_picture_url} name={r.name || 'Unknown'} subtext={r.username ? `@${r.username}` : r.email} />,
    },
    {
      key: 'specialties',
      header: 'Specialties',
      cell: (r) =>
        r.trainer_specialties?.length ? (
          <div className="flex flex-wrap gap-1">
            {r.trainer_specialties.slice(0, 2).map((s, i) => (
              <span key={i} className="rounded-pill bg-surface-2 px-2 py-0.5 text-xs font-medium text-ink-2">
                {s}
              </span>
            ))}
            {r.trainer_specialties.length > 2 && (
              <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-xs font-medium text-ink-2">
                +{r.trainer_specialties.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
    { key: 'rate', header: 'Rate/hr', align: 'right', cell: (r) => <Money value={r.trainer_hourly_rate} /> },
    { key: 'sessions_completed', header: 'Sessions', sortable: true, align: 'right', cell: (r) => <span className="tabular-nums">{r.sessions_completed || 0}</span> },
    { key: 'average_rating', header: 'Rating', sortable: true, cell: (r) => (r.average_rating ? <StarsCell value={r.average_rating} /> : <span className="text-ink-3">No reviews</span>) },
    { key: 'status', header: 'Status', cell: (r) => <TrainerStatusBadge trainer={r} /> },
  ];
  return (
    <ListScreen
      stats={<TrainerStats />}
      tabs={<TrainerStatusTabs />}
      actions={<SortMenu fields={TRAINER_SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={TRAINER_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No trainers found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

// Trainer List Component
export const TrainerList = (props: any) => (
  <List
    {...props}
    actions={false}
    empty={false}
    pagination={false}
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
    component="div"
  >
    <SearchIntegration filterKey="search" />
    <TrainerListBody />
  </List>
);

// Media Viewer Modal Component
const MediaViewerModal = ({
  src,
  alt,
  type = 'image',
  open,
  onClose,
}: {
  src: string;
  alt: string;
  type?: 'image' | 'video';
  open: boolean;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error(LogCategory.UI, 'Failed to copy text', err, 'Trainers');
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>{type === 'video' ? 'Video' : 'Image'}</span>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4">
          <div className="flex justify-center">
            {type === 'video' ? (
              <video
                src={src}
                controls
                className="max-w-full max-h-96 rounded-lg"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={src}
                alt={alt}
                className="max-w-full max-h-96 object-contain rounded-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-black">URL:</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
              <code className="flex-1 text-sm text-gray-800 break-all">{src}</code>
              <div className="flex gap-1">
                <button
                  onClick={() => copyToClipboard(src)}
                  className="p-1 rounded border hover:bg-gray-100 flex items-center justify-center"
                  title="Copy URL"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  onClick={() => openInNewTab(src)}
                  className="p-1 rounded border hover:bg-gray-100 flex items-center justify-center"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            </div>
            {copied && (
              <p className="text-xs text-green-600">URL copied to clipboard!</p>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Image Gallery Component
const ImageGallery = ({ urls, title }: { urls: string[]; title: string }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (!urls || urls.length === 0) {
    return <p className="text-sm text-gray-500">No {title.toLowerCase()} provided</p>;
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {urls.map((url, index) => (
          <div
            key={index}
            className="relative aspect-square rounded-lg overflow-hidden border cursor-pointer"
            onClick={() => setSelectedImage(url)}
          >
            <img
              src={url}
              alt={`${title} ${index + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        ))}
      </div>
      {selectedImage && (
        <MediaViewerModal
          src={selectedImage}
          alt={title}
          type="image"
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};

// Trainer Show Content Component
const TrainerShowContent = () => {
  const { record, isLoading } = useShowContext<TrainerDetails>();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [application, setApplication] = useState<TrainerApplication | null>(null);
  const [applicationLoading, setApplicationLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<{ src: string; alt: string; type: 'image' | 'video' } | null>(null);

  // Fetch trainer application data
  useEffect(() => {
    if (record?.id) {
      setApplicationLoading(true);
      dataProvider
        .getList('trainer-applications', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'created_at', order: 'DESC' },
          filter: { user_id: record.id },
        })
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setApplication(response.data[0] as TrainerApplication);
          }
          setApplicationLoading(false);
        })
        .catch((error) => {
          logger.error(LogCategory.API, 'Failed to fetch application', error, 'Trainers');
          setApplicationLoading(false);
        });
    }
  }, [record?.id, dataProvider]);

  if (isLoading) {
    return <div className="p-6 text-center">Loading trainer details...</div>;
  }

  if (!record) {
    return <div className="p-6 text-center">Trainer not found</div>;
  }

  const handleViewApplication = () => {
    if (application?.id) {
      navigate(`/trainer-applications/${application.id}/show`);
    } else {
      notify('Application not found', { type: 'warning' });
    }
  };

  // Parse JSON arrays for certificates and class photos
  const certificateUrls = application?.certificate_urls
    ? (typeof application.certificate_urls === 'string'
        ? JSON.parse(application.certificate_urls)
        : application.certificate_urls)
    : [];

  const classPhotoUrls = application?.class_photo_urls
    ? (typeof application.class_photo_urls === 'string'
        ? JSON.parse(application.class_photo_urls)
        : application.class_photo_urls)
    : [];

  return (
    <div className="p-6 space-y-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={record.profile_picture_url || undefined} sx={{ width: 60, height: 60 }}>
            {record.name?.charAt(0).toUpperCase() || 'T'}
          </Avatar>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.name || 'Unknown'}
              </h1>
              <TrainerStatusBadge trainer={record} />
            </div>
            <p className="mt-1 text-sm text-ink-2">
              {record.email}
              {record.username && ` · @${record.username}`}
            </p>
          </div>
        </div>
        <button
          onClick={handleViewApplication}
          className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
        >
          <FileText className="size-4" />
          View Application
        </button>
      </div>

      {/* Status Banner */}
      {(record.is_suspended || record.is_banned || !record.profile_completed_at) && (
        <Card className={`border-2 ${
          record.is_banned
            ? 'border-black bg-gray-900'
            : record.is_suspended
            ? 'border-purple-200 bg-purple-50'
            : 'border-yellow-200 bg-yellow-50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {record.is_banned ? (
                  <Ban className="w-5 h-5 text-white" />
                ) : record.is_suspended ? (
                  <Pause className="w-5 h-5 text-purple-700" />
                ) : (
                  <Clock className="w-5 h-5 text-yellow-700" />
                )}
                <span className={`font-medium ${
                  record.is_banned ? 'text-white'
                  : record.is_suspended ? 'text-purple-900'
                  : 'text-yellow-900'
                }`}>
                  {record.is_banned ? 'Account Banned'
                    : record.is_suspended ? 'Account Suspended'
                    : 'Profile Incomplete - Pending Onboarding'}
                </span>
              </div>
            </div>
            {(record.suspension_reason || record.is_banned) && (
              <div className={`mt-2 text-sm ${record.is_banned ? 'text-gray-200' : 'text-purple-800'}`}>
                <strong>Reason:</strong> {record.suspension_reason || 'Not specified'}
              </div>
            )}
            {record.suspension_until && (
              <div className="mt-1 text-sm text-purple-800">
                <strong>Until:</strong> {new Date(record.suspension_until).toLocaleDateString()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Trainer Profile Card */}
        <Card className="border border-border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
              <User className="w-5 h-5" />
              Trainer Profile
            </h3>
            <div className="flex items-center space-x-4 mb-4">
              <Avatar
                src={record.profile_picture_url || undefined}
                sx={{ width: 80, height: 80 }}
              >
                {record.name?.charAt(0).toUpperCase() || 'T'}
              </Avatar>
              <div>
                <h4 className="text-xl font-semibold text-black">
                  {record.name || 'Unknown'}
                </h4>
                <p className="text-sm text-gray-600">@{record.username}</p>
                <p className="text-black">{record.email}</p>
                {record.phone && <p className="text-sm text-gray-600">{record.phone}</p>}
              </div>
            </div>
            {record.trainer_bio && (
              <div className="mt-4">
                <label className="text-sm font-medium text-black">Bio</label>
                <p className="text-sm text-gray-700 mt-1">{record.trainer_bio}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Details Card */}
        <Card className="border border-border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
              <Award className="w-5 h-5" />
              Training Details
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-black">Specialties</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {record.trainer_specialties?.length > 0 ? (
                    record.trainer_specialties.map((specialty, index) => (
                      <Badge
                        key={index}
                        className="bg-blue-50 text-blue-700 border border-blue-200"
                      >
                        {specialty}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">Not specified</span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-black">Hourly Rate</label>
                <p className="text-sm text-black mt-1">
                  {record.trainer_hourly_rate ? `$${record.trainer_hourly_rate}` : 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-black">Availability</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {record.trainer_availability_days?.length > 0 ? (
                    record.trainer_availability_days.map((day, index) => (
                      <Badge
                        key={index}
                        className="bg-green-50 text-green-700 border border-green-200"
                      >
                        {day}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">Not specified</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Session Statistics */}
        <Card className="border border-border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Session Statistics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Total Sessions</label>
                <p className="text-2xl font-bold text-black">{record.total_sessions || 0}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Completed</label>
                <p className="text-2xl font-bold text-green-600">{record.completed_sessions || 0}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Cancelled</label>
                <p className="text-2xl font-bold text-red-600">{record.cancelled_sessions || 0}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Completion Rate</label>
                <p className="text-2xl font-bold text-black">
                  {record.total_sessions > 0
                    ? `${Math.round((record.completed_sessions / record.total_sessions) * 100)}%`
                    : '0%'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rating & Reviews */}
        <Card className="border border-border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
              <Star className="w-5 h-5" />
              Rating & Reviews
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-black">
                    {record.average_rating ? record.average_rating.toFixed(1) : '-'}
                  </div>
                  <div className="flex items-center justify-center mt-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 text-gray-300" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600">
                    Based on <strong>{record.total_reviews || 0}</strong> reviews
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card className="md:col-span-2 border border-border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Account Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Member Since</label>
                <p className="text-sm text-black mt-1">
                  {new Date(record.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Profile Completed</label>
                <p className="text-sm text-black mt-1">
                  {record.profile_completed_at
                    ? new Date(record.profile_completed_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'Incomplete'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Account Status</label>
                <div className="mt-1">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getOnboardingStatusStyle(record)}`}>
                    {getOnboardingStatusIcon(record)}
                    {getOnboardingStatusText(record)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Application Documents */}
        {applicationLoading ? (
          <Card className="md:col-span-2 border border-border bg-card">
            <CardContent className="p-6">
              <p className="text-center text-gray-500">Loading application documents...</p>
            </CardContent>
          </Card>
        ) : application ? (
          <>
            {/* Application Bio */}
            {application.bio && (
              <Card className="md:col-span-2 border border-border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Application Bio
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{application.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* ID Documents */}
            <Card className="md:col-span-2 border border-border bg-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  ID Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ID Front */}
                  <div>
                    <label className="text-sm font-medium text-black mb-2 block">ID Document (Front)</label>
                    {application.id_document_front_url ? (
                      <div
                        className="relative aspect-video rounded-lg overflow-hidden border cursor-pointer bg-gray-50"
                        onClick={() =>
                          setSelectedMedia({
                            src: application.id_document_front_url,
                            alt: 'ID Document Front',
                            type: 'image',
                          })
                        }
                      >
                        <img
                          src={application.id_document_front_url}
                          alt="ID Front"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Not provided</p>
                    )}
                  </div>

                  {/* ID Back */}
                  <div>
                    <label className="text-sm font-medium text-black mb-2 block">ID Document (Back)</label>
                    {application.id_document_back_url ? (
                      <div
                        className="relative aspect-video rounded-lg overflow-hidden border cursor-pointer bg-gray-50"
                        onClick={() =>
                          setSelectedMedia({
                            src: application.id_document_back_url,
                            alt: 'ID Document Back',
                            type: 'image',
                          })
                        }
                      >
                        <img
                          src={application.id_document_back_url}
                          alt="ID Back"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Not provided</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Introduction Video */}
            {application.video_url && (
              <Card className="md:col-span-2 border border-border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Introduction Video
                  </h3>
                  <div
                    className="relative aspect-video rounded-lg overflow-hidden border cursor-pointer bg-gray-50"
                    onClick={() =>
                      setSelectedMedia({
                        src: application.video_url!,
                        alt: 'Introduction Video',
                        type: 'video',
                      })
                    }
                  >
                    <video
                      src={application.video_url}
                      className="w-full h-full object-contain"
                      controls={false}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                      <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                        <div className="w-0 h-0 border-l-8 border-t-6 border-b-6 border-transparent border-l-black ml-1" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certificates */}
            {certificateUrls.length > 0 && (
              <Card className="md:col-span-2 border border-border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Certificates ({certificateUrls.length})
                  </h3>
                  <ImageGallery urls={certificateUrls} title="Certificates" />
                </CardContent>
              </Card>
            )}

            {/* Class Photos */}
            {classPhotoUrls.length > 0 && (
              <Card className="md:col-span-2 border border-border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Class Photos ({classPhotoUrls.length})
                  </h3>
                  <ImageGallery urls={classPhotoUrls} title="Class Photos" />
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="md:col-span-2 border border-border bg-card">
            <CardContent className="p-6">
              <p className="text-center text-gray-500">No application documents found for this trainer.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Specialty Certificates Section (FEAT-028, per-sport — distinct from
          the application-level "Certificates" gallery above) */}
      <TrainerSpecialtyCertificatesSection trainerId={record.id} />

      {/* Trainer Sessions Section */}
      <TrainerSessionsSection trainerId={record.id} />

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <MediaViewerModal
          src={selectedMedia.src}
          alt={selectedMedia.alt}
          type={selectedMedia.type}
          open={!!selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
};

// Trainer Sessions Section ───────────────────────────────────────────
const TrainerSpecialtyCertificatesSection: React.FC<{ trainerId: number | string }> = ({
  trainerId,
}) => {
  const { data, isLoading, error } = useGetList<SpecialtyCertificate>('specialty-certificates', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'created_at', order: 'DESC' },
    filter: { trainer_id: trainerId },
  });

  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
          <Award className="w-5 h-5" />
          Specialty Certificates
        </h3>

        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {!isLoading && error && (
          <p className="text-sm text-red-700">Failed to load specialty certificates.</p>
        )}
        {!isLoading && !error && (!data || data.length === 0) && (
          <p className="text-sm text-gray-500">No specialty certificates submitted.</p>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Sport</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Submitted</th>
                  <th className="py-2 pr-4 text-right">Documents</th>
                  <th className="py-2 pl-4" />
                </tr>
              </thead>
              <tbody>
                {data.map((cert) => {
                  const docCount = (() => {
                    try {
                      return cert.document_urls ? JSON.parse(cert.document_urls).length : 0;
                    } catch {
                      return 0;
                    }
                  })();
                  return (
                    <tr key={cert.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-2 pr-4 text-black">
                        {cert.sport?.name_es || cert.sport?.name_en || '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <CertStatusBadge status={cert.status} />
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{fmtDate(cert.created_at)}</td>
                      <td className="py-2 pr-4 text-right text-gray-700">{docCount}</td>
                      <td className="py-2 pl-4 text-right">
                        <Link
                          to={`/specialty-certificates/${cert.id}/show`}
                          className="text-sm font-semibold text-indigo hover:underline"
                        >
                          Review
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TrainerSessionsSection: React.FC<{ trainerId: number | string }> = ({ trainerId }) => {
  const dataProvider = useDataProvider() as unknown as CustomDataProvider;
  const [sessions, setSessions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 10;

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, total } = await dataProvider.getTrainerSessions({
        id: trainerId as any,
        page,
        limit,
        status: status || undefined,
      });
      setSessions(data || []);
      setTotal(total ?? 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load trainer sessions');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, trainerId, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-black">Sessions</h3>
          <div className="flex items-center gap-2">
            <select
              className="text-sm border rounded px-2 py-1 bg-white"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              disabled={loading}
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="full">Full</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}

        {!loading && !error && sessions.length === 0 && (
          <p className="text-sm text-gray-500">No sessions for this trainer.</p>
        )}

        {!loading && !error && sessions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Participants</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-2 pr-4 text-black">
                      <Link to={`/sessions/${s.id}/show`}>#{s.id}</Link>
                    </td>
                    <td className="py-2 pr-4 text-black">{s.title || s.session_title || '—'}</td>
                    <td className="py-2 pr-4 text-gray-700">
                      {s.date_time || s.scheduled_at ? new Date(s.date_time || s.scheduled_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{s.session_type || '—'}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={s.status === 'cancelled' ? 'destructive' : 'secondary'}>
                        {s.status || '—'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-700">
                      {s.current_participants ?? 0}
                      {s.max_participants ? ` / ${s.max_participants}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-600">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Trainer Show Component
export const TrainerShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <TrainerShowContent />
  </Show>
);
